import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import cn from "classnames";
import { useUI } from "../context";
import { Step1 } from "./steps/step1";
import { Step2 } from "./steps/step2";
import { Step3 } from "./steps/step3";
import { Step4 } from "./steps/step4";
import { Step5 } from "./steps/step5";
import { Footer } from "./footer";

const { page_settings } = window.CONTACT_FORM || { page_settings: {} };

const validationSchema = !window.CONTACT_FORM
  ? []
  : [
      yup.object(),
      yup.object(),
      yup.object({
        [page_settings.step3.fieldActivity]: yup.string().required(),
        [page_settings.step3.fieldCompanyNumber]: yup.string().when("no-company-number", {
          is: (v) => v === false,
          then: () =>
            yup.string().test("len", "Must be exactly 10 characters", (val) => val.length === 10),
        }),
        [page_settings.step3.fieldDesiredDestination]: yup
          .string()
          .when(page_settings.step2.fieldObjective, {
            is: (v) => v === page_settings.step2.valueCheckbox1,
            then: () => yup.string().required(),
          }),
        [page_settings.step3.fieldCompanyName]: yup
          .string()
          .when(page_settings.step3.fieldCheckboxes, {
            is: (v) => v === page_settings.step3.valueCheckbox2,
            then: () => yup.string().required(),
          }),
        [page_settings.step3.fieldSocial]: yup.string().when(page_settings.step3.fieldCheckboxes, {
          is: (v) => v === page_settings.step3.valueCheckbox2,
          then: () => yup.string().required(),
        }),
        [page_settings.step3.fieldYourFunction]: yup
          .string()
          .when([page_settings.step3.fieldCheckboxes, page_settings.step2.fieldObjective], {
            is: (v1, v2) =>
              v1 === page_settings.step3.valueCheckbox2 ||
              v2 === page_settings.step2.valueCheckbox2,
            then: () => yup.string().required(),
          }),
        [page_settings.step3.fieldNameAsb]: yup.string().when(page_settings.step2.fieldObjective, {
          is: (v) => v === page_settings.step2.valueCheckbox2,
          then: () => yup.string().required(),
        }),
      }),
      yup.object({
        [page_settings.step4.fieldName]: yup.string().required(),
        [page_settings.step4.fieldFirstName]: yup.string().required(),
        [page_settings.step4.fieldPhoneNumber]: yup.string().required(),
        [page_settings.step4.fieldLanguage]: yup.string().required(),
        [page_settings.step4.fieldEmail]: yup
          .string()
          .email()
          .matches(/@[^.]*\./),
        [page_settings.step4.fieldAddress]: yup.string().required(),
      }),
    ];

const StepContainer = ({ visible, children }) => (
  <div
    className={cn("block flex flex-col gap-6", {
      hidden: !visible,
    })}
  >
    {children}
  </div>
);

const steps = [Step1, Step2, Step3, Step4, Step5];

export const Form = () => {
  const { activeStep, nextStep, setIsLoading, products } = useUI();

  const formMethods = useForm({
    resolver: yupResolver(validationSchema[activeStep]),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = formMethods;

  const onSubmit = (data) => {
    if (activeStep < 3) return nextStep();

    setIsLoading(true);
    return fetch("/wp-json/v1/contact", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...data, ...products[0].meta }),
    })
      .then(() => {
        nextStep();
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...formMethods}>
        {steps.map((StepComponent, index) => (
          <StepContainer key={index} visible={activeStep === index}>
            <StepComponent register={register} watch={watch} errors={errors} />
          </StepContainer>
        ))}
        <Footer />
      </FormProvider>
    </form>
  );
};
